// ─── PatternInterpretation ───────────────────────────────────────────────────
//
// Dashboard section that surfaces the currently detected phenotype pattern
// (from the existing phenotype engine). This is the concise on-dashboard
// view — distinct from the full /phenotype page which shows every grading
// result with citations. Here we show:
//   - the primary detected pattern (name + short description)
//   - the supporting signals (up to 5)
//   - data that's missing but would strengthen / refute the pattern
//   - alternative explanations (clinician-only)
//   - confidence band
//
// Visual hierarchy matches TodaysPrimaryInsight: thick left accent border,
// text-xl semibold pattern name headline, signal-strength progress bar, a
// dedicated accent-tinted header strip so the section can never blend into
// surrounding cards visually. When the engine has no primary phenotype the
// component renders a `PatternBaselineCard` instead of null — same visual
// weight, "Building baseline" chip, "No dominant pattern yet" headline —
// so the slot between System Overview and Mental Health is always present
// and the user can see the section exists even before a pattern emerges.
//
// All copy is lens-aware: patient gets the "what this suggests for you"
// framing sourced from phenotypeInterpretations.tsx; clinician gets the
// clinical interpretation + alternative explanations.
//
// This component is presentation-only. It consumes the PatternInterpretation
// view model produced by computePatternInterpretation() in the transformer
// layer — no business logic lives here.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import type {
  PatternInterpretation as PatternInterpretationVM,
  ConfidenceLevel,
} from '../../lib/dashboard/types';
import { useLens } from '../../lib/dashboard/lensContext';
import { pickLens, confidenceLabel } from '../../lib/dashboard/lensCopy';

interface Props {
  data: PatternInterpretationVM;
}

// ─── Visual helpers ──────────────────────────────────────────────────────────

const CONFIDENCE_CHIP: Record<ConfidenceLevel, { bg: string; text: string; dot: string; label: string }> = {
  high:         { bg: 'bg-teal-50 dark:bg-teal-900/30',   text: 'text-teal-700 dark:text-teal-300',   dot: 'bg-teal-500',  label: 'High confidence' },
  moderate:     { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500', label: 'Moderate confidence' },
  low:          { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500', label: 'Low confidence' },
  insufficient: { bg: 'bg-gray-50 dark:bg-white/5',       text: 'text-gray-600 dark:text-gray-400',   dot: 'bg-gray-400',  label: 'Insufficient data' },
};

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function SignalStrengthBar({ score, color }: { score: number; color: string }) {
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div
      className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={`Pattern signal strength: ${pct} of 100`}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ─── Baseline / undetected card ──────────────────────────────────────────────

function PatternBaselineCard({ data }: { data: PatternInterpretationVM }) {
  const { lens } = useLens();
  const accent = '#6F8A5A'; // arete-moss fallback — pattern not yet detected
  const headerTint = hexToRgba(accent, 0.08);
  const headingId = 'pattern-interpretation-baseline-heading';

  return (
    <section
      aria-labelledby={headingId}
      data-testid="pattern-interpretation-section"
      data-pattern-state="baseline"
      className="card !p-0 overflow-hidden"
      style={{ borderLeft: `5px solid ${accent}` }}
    >
      <div className="px-7 pt-6 pb-5" style={{ background: headerTint }}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <p className="section-label" style={{ color: accent }}>
            Pattern Interpretation
          </p>
          <div className="flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-medium bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle bg-gray-400"
              aria-hidden="true"
            />
            Building baseline
          </div>
        </div>
        <h2
          id={headingId}
          className="text-xl font-semibold text-arete-navy dark:text-gray-100 leading-tight"
        >
          No dominant pattern yet
        </h2>
        <p className="mt-2 text-sm text-arete-navy/80 dark:text-gray-300 leading-relaxed">
          {pickLens(data.summary, lens)}
        </p>
      </div>
      {data.missingData.length > 0 && (
        <div className="px-7 py-5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-arete-muted mb-2">
            What would help us detect a pattern
          </p>
          <ul className="flex flex-col gap-1 text-sm text-arete-navy/80 dark:text-gray-200">
            {data.missingData.slice(0, 4).map((m) => (
              <li key={m.id} className="flex items-baseline gap-2.5">
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: accent }}
                  aria-hidden="true"
                />
                <span>{m.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function PatternInterpretation({ data }: Props) {
  const { lens } = useLens();
  const [showAlternatives, setShowAlternatives] = useState(false);

  // IMPORTANT: this component MUST always render a visible section. It
  // previously returned null when no primary phenotype was detected, which
  // hid the whole section on real-user accounts that hadn't yet crossed the
  // detection threshold. That made it look like the section was never
  // shipped. If there is no primary pattern, render a visible
  // PatternBaselineCard so the slot between System Overview and Mental
  // Health is always present and positively identifiable in the DOM via
  // data-testid="pattern-interpretation-section".
  if (!data.detected || !data.primary) {
    return <PatternBaselineCard data={data} />;
  }

  const primary = data.primary;
  const chip = CONFIDENCE_CHIP[data.confidence];
  const confLabel = pickLens(confidenceLabel(data.confidence), lens);
  const headingId = `pattern-interpretation-${primary.id}`;
  const accent = primary.color ?? '#6F8A5A';
  const headerTint = hexToRgba(accent, 0.08);

  return (
    <section
      aria-labelledby={headingId}
      data-testid="pattern-interpretation-section"
      data-pattern-state="detected"
      data-pattern-id={primary.id}
      className="card !p-0 overflow-hidden"
      style={{ borderLeft: `5px solid ${accent}` }}
    >
      {/* ── Tinted header strip ────────────────────────────────────── */}
      <div
        className="px-7 pt-6 pb-5"
        style={{ background: headerTint }}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <p
            className="section-label"
            style={{ color: accent }}
          >
            Pattern Interpretation
          </p>
          <div
            className={`flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-medium ${chip.bg} ${chip.text}`}
            title={confLabel}
          >
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${chip.dot}`}
              aria-hidden="true"
            />
            {chip.label}
          </div>
        </div>

        {/* Primary pattern headline — matches TodaysPrimaryInsight weight */}
        <h2
          id={headingId}
          className="text-xl font-semibold text-arete-navy dark:text-gray-100 leading-tight"
        >
          {primary.name}
        </h2>

        {/* Signal strength pill + score + bar */}
        <div className="mt-3 flex items-center gap-3">
          <span
            className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ backgroundColor: hexToRgba(accent, 0.18), color: accent }}
          >
            {primary.level} signal
          </span>
          <span className="text-[11px] font-medium text-arete-muted">
            {primary.score}/100
          </span>
          <div className="flex-1 max-w-[160px]">
            <SignalStrengthBar score={primary.score} color={accent} />
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────── */}
      <div className="px-7 py-6">
        {/* Lens-aware summary */}
        <p className="text-sm text-arete-navy/85 dark:text-gray-200 leading-relaxed">
          {pickLens(data.summary, lens)}
        </p>

        {/* Supporting signals */}
        {data.supportingSignals.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-arete-muted mb-2">
              {lens === 'patient' ? 'What we looked at' : 'Supporting signals'}
            </p>
            <ul className="flex flex-col gap-2">
              {data.supportingSignals.map((s) => (
                <li
                  key={s.id}
                  className="flex items-baseline gap-2.5 text-sm text-arete-navy/90 dark:text-gray-200"
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: accent }}
                    aria-hidden="true"
                  />
                  <span className="font-medium">
                    {lens === 'clinician' && s.labelClinician ? s.labelClinician : s.label}
                  </span>
                  {s.value && s.value !== '—' && (
                    <>
                      <span className="text-arete-muted">·</span>
                      <span className="text-arete-muted">{s.value}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Missing data */}
        {data.missingData.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-arete-muted mb-2">
              What would strengthen this view
            </p>
            <ul className="flex flex-col gap-1 text-xs text-arete-navy/80 dark:text-gray-300">
              {data.missingData.slice(0, 4).map((m) => (
                <li key={m.id} className="flex items-baseline gap-2">
                  <span
                    className="inline-block w-1 h-1 rounded-full bg-arete-muted flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span>{m.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Alternative explanations — clinician only, collapsible */}
        {lens === 'clinician' && data.alternativeExplanations.length > 0 && (
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-white/10">
            <button
              type="button"
              onClick={() => setShowAlternatives((v) => !v)}
              aria-expanded={showAlternatives}
              className="text-[11px] font-medium text-arete-blue hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40 rounded px-1"
            >
              {showAlternatives
                ? 'Hide alternative explanations'
                : 'Show alternative explanations to consider'}
            </button>
            {showAlternatives && (
              <ul className="mt-2 flex flex-col gap-1 text-xs text-arete-muted leading-relaxed border-l-2 border-arete-sage/40 pl-3">
                {data.alternativeExplanations.map((alt, i) => (
                  <li key={i}>{alt}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
